import { createSelector } from 'selectors'
import { cn } from 'utils'

import { constant } from './constant'
import { getStickyBlockSelectors } from './selector'

export const StickyBlock = (() => {
	type StickyBlockWrapper = {
		init: () => void
		destroy: () => void
	}

	let wrappers: StickyBlockWrapper[] = []

	const removeShadowFromBlock = (block: HTMLElement) => {
		const blockWithShadow = createSelector<HTMLElement>({
			scope: block,
			selector: constant.selector.isShadow,
		})
		if (!blockWithShadow) {
			return
		}

		cn.removeClass(blockWithShadow, constant.selector.isShadow.replace('.', ''))
	}

	const getHeightOfBlocksAbove = (currentBlock: HTMLElement, stickyBlocks: NodeListOf<HTMLElement>) => {
		const blockRect = currentBlock.getBoundingClientRect()

		const rowHeights = new Map<number, number>()

		stickyBlocks.forEach((element: HTMLElement) => {
			if (element === currentBlock) {
				return
			}

			const elementRect = element.getBoundingClientRect()

			if (elementRect.top > blockRect.top || elementRect.y === blockRect.y) {
				return
			}

			const row = elementRect.top

			if (!rowHeights.has(row)) {
				rowHeights.set(row, elementRect.height)
			} else {
				rowHeights.set(row, Math.max(elementRect.height, rowHeights.get(row) as number))
			}
		})

		return Array.from(rowHeights.values()).reduce((x, y) => x + y, 0)
	}

	const createWrapper = (
		stickyBlocks: NodeListOf<HTMLElement>,
		stickyBox: HTMLElement,
		block: HTMLElement,
	): StickyBlockWrapper => {
		const createPlaceholder = () => {
			const placeholder = block.cloneNode(false) as HTMLElement
			placeholder.removeAttribute('id')
			placeholder.removeAttribute('data-resize-id')
			placeholder.removeAttribute('data-block-editor')
			placeholder.classList.remove('js-resize-item')
			placeholder.classList.remove('grid-stack-item')
			placeholder.hidden = true

			return placeholder
		}

		let placeholder: HTMLElement
		let events = [] as { type: string; callback: (e: any) => void }[]

		const unstickBlock = () => {
			block.classList.remove('!fixed')
			block.classList.remove('m-2')
			block.style.zIndex = ''
			block.style.top = ''
			block.style.width = ''
			block.style.left = ''

			placeholder.hidden = true
		}

		const init = () => {
			events = []

			placeholder = createPlaceholder()

			const parent = block.parentElement as HTMLElement
			parent.insertBefore(placeholder, block)

			removeShadowFromBlock(block)

			const heightOfBlocksAbove = getHeightOfBlocksAbove(block, stickyBlocks)

			const blockComputedStyles = getComputedStyle(block)
			const { maxWidth } = blockComputedStyles

			let parentStyle = parent.getBoundingClientRect()
			const leftOffsetPercent = block.offsetLeft / parentStyle.width

			const updateBlockLeftOffset = () => {
				if (leftOffsetPercent !== 0) {
					block.style.left = `${parentStyle.width * leftOffsetPercent}px`
				}
			}

			const registerEvent = (type: string, callback: (e: any) => void) => {
				events.push({ type, callback })

				window.addEventListener(type, callback)
			}

			const scrollHandler = () => {
				const stickyBoxRect = stickyBox.getBoundingClientRect()

				const blockRect = (placeholder.hidden ? block : placeholder).getBoundingClientRect()

				const diff = stickyBoxRect.bottom + heightOfBlocksAbove - blockRect.top

				if (diff > 0) {
					if (!block.classList.contains('!fixed')) {
						block.style.width = `${parseInt(blockComputedStyles.width, 10)}px`
						block.style.left = `${parseInt(blockComputedStyles.left, 10)}px`
					}
					block.classList.add('!fixed')
					block.classList.add('m-2')
					block.style.zIndex = '8'
					block.style.top = `${stickyBoxRect.bottom + heightOfBlocksAbove}px`
					block.style.width = maxWidth
					updateBlockLeftOffset()

					placeholder.hidden = false
				} else {
					unstickBlock()
				}
			}
			registerEvent('scroll', scrollHandler)

			const resizeHandler = () => {
				parentStyle = parent.getBoundingClientRect()
				window.location.reload()
			}
			registerEvent('resize', resizeHandler)
		}

		const destroy = () => {
			events.forEach((e: { type: any; callback: (this: Window, ev: any) => any }) =>
				window.removeEventListener(e.type, e.callback),
			)
			placeholder.remove()
			unstickBlock()
		}

		return {
			init,
			destroy,
		}
	}

	const init = (isAjax?: boolean) => {
		if (isAjax) {
			wrappers.forEach((wrapper) => wrapper.destroy())
		}

		const { stickyBlocks } = getStickyBlockSelectors()
		if (stickyBlocks.length === 0) {
			return
		}

		const { stickyBox } = getStickyBlockSelectors()

		wrappers = Array.from(stickyBlocks).map((stickyBlock) => createWrapper(stickyBlocks, stickyBox, stickyBlock))
		wrappers.forEach((wrapper) => wrapper.init())
	}

	return {
		init,
	}
})()
